import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Keyboard, Camera, X, Loader2, Globe, User } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "@/hooks/use-toast";
import { useAssignCleaner } from "@/hooks/useAssignCleaner";
import { useUpdateProfileLanguage } from "@/hooks/useProfileLanguage";
import { useTranslation } from "@/hooks/useTranslation";
import { LanguageSelector } from "@/components/cleaner/LanguageSelector";
import { LanguageCode, getStoredLanguage } from "@/lib/languages";

const CleanerOnboarding = () => {
  const navigate = useNavigate();
  const { t, changeLanguage, lang, isRtl } = useTranslation();
  const { user, profile, loading, signOut, refetchProfile } = useAuth();
  
  // Determine initial step based on whether user has first_name
  const needsName = !profile?.first_name;
  const [step, setStep] = useState<"name" | "language" | "property">(needsName ? "name" : "language");
  
  const [firstName, setFirstName] = useState(profile?.first_name || "");
  const [lastName, setLastName] = useState(profile?.last_name || "");
  const [isSavingName, setIsSavingName] = useState(false);
  
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>(getStoredLanguage);
  const [showScanner, setShowScanner] = useState(false);
  const [showManualInput, setShowManualInput] = useState(false);
  const [propertyCode, setPropertyCode] = useState("");
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const { status, assignCurrentCleanerByCode, assignCurrentCleanerToProperty } = useAssignCleaner();
  const updateLanguage = useUpdateProfileLanguage({ reloadOnSuccess: false });

  // Handle language change - update localStorage and UI immediately
  const handleLanguageChange = (newLang: LanguageCode) => {
    setSelectedLanguage(newLang);
    changeLanguage(newLang); // Instant UI update
  };

  // Update step when profile loads
  useEffect(() => {
    if (profile && !profile.first_name && step !== "name") {
      setStep("name");
    } else if (profile?.first_name && step === "name") {
      setStep("language");
    }
  }, [profile?.first_name]);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login", { replace: true });
      return;
    }

    if (!loading && profile && profile.role !== "cleaner") {
      navigate("/", { replace: true });
    }
  }, [user, profile, loading, navigate]);

  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const handleNameSubmit = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast({
        title: t("nameRequired") || "Name required",
        description: t("enterFirstLastName") || "Please enter your first and last name.",
        variant: "destructive",
      });
      return;
    }

    setIsSavingName(true);
    try {
      const fullName = `${firstName.trim()} ${lastName.trim()}`;
      
      // Update profile
      const { error: profileError } = await supabase
        .from("profiles")
        .update({ 
          first_name: firstName.trim(), 
          last_name: lastName.trim(),
          full_name: fullName
        })
        .eq("id", user!.id);

      if (profileError) throw profileError;

      // Also update cleaner record name
      const { error: cleanerError } = await supabase
        .from("cleaners")
        .update({ name: fullName })
        .eq("user_id", user!.id);

      if (cleanerError) console.error("Error updating cleaner name:", cleanerError);

      await refetchProfile();
      setStep("language");
    } catch (error: any) {
      toast({
        title: t("errorSavingName") || "Error saving name",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSavingName(false);
    }
  };

  const handleLanguageSubmit = async () => {
    try {
      await updateLanguage.mutateAsync(selectedLanguage);
      setStep("property");
    } catch (error: any) {
      toast({
        title: t("errorSavingLanguage"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const startScanner = async () => {
    setShowScanner(true);
    setShowManualInput(false);

    // Small delay to ensure DOM element is rendered
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      // Request camera permission explicitly first
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: "environment" } 
      });
      // Stop the stream immediately - we just needed to request permission
      stream.getTracks().forEach(track => track.stop());

      const html5QrCode = new Html5Qrcode("qr-reader");
      scannerRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: "environment" },
        { 
          fps: 10, 
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0
        },
        (decodedText) => {
          html5QrCode.stop().catch(() => {});
          setShowScanner(false);
          handleScannedUrl(decodedText);
        },
        () => {}
      );
    } catch (err: any) {
      console.error("Error starting scanner:", err);
      const errorMessage = err?.name === "NotAllowedError" 
        ? (t("cameraPermissionDenied") || "Camera permission denied. Please allow camera access in your browser settings.")
        : (t("cameraErrorDesc") || "Could not access the camera. Please try again or enter the code manually.");
      toast({
        title: t("cameraError") || "Camera Error",
        description: errorMessage,
        variant: "destructive",
      });
      setShowScanner(false);
    }
  };

  const stopScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {});
    }
    setShowScanner(false);
  };

  const handleScannedUrl = async (url: string) => {
    try {
      const parsedUrl = new URL(url);
      const pathname = parsedUrl.pathname;

      let propertyId: string | null = null;

      if (pathname.startsWith("/register-cleaner/")) {
        propertyId = pathname.replace("/register-cleaner/", "");
      } else if (pathname.startsWith("/clean/")) {
        propertyId = pathname.replace("/clean/", "");
      }

      // CRITICAL: Validate propertyId is valid UUID before calling
      if (propertyId && propertyId.match(/^[a-f0-9-]{36}$/i)) {
        // Additional validation - ensure not null/undefined
        if (propertyId === null || propertyId === undefined || propertyId.trim() === '') {
          toast({
            title: t("invalidQrCode"),
            description: "Invalid property ID format",
            variant: "destructive",
          });
          return;
        }
        await assignCurrentCleanerToProperty(propertyId);
      } else {
        toast({
          title: t("invalidQrCode"),
          description: t("invalidQrCodeDesc"),
          variant: "destructive",
        });
      }
    } catch {
      // CRITICAL: Validate URL is valid UUID before calling
      if (url.match(/^[a-f0-9-]{36}$/i)) {
        if (url === null || url === undefined || url.trim() === '') {
          toast({
            title: t("invalidQrCode"),
            description: "Invalid property ID format",
            variant: "destructive",
          });
          return;
        }
        await assignCurrentCleanerToProperty(url);
      } else {
        toast({
          title: t("invalidQrCode"),
          description: t("invalidQrCodeRetry"),
          variant: "destructive",
        });
      }
    }
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const code = propertyCode.trim();
    
    // CRITICAL: Validate code is not null/empty
    if (!code || code === null || code === undefined || code.trim() === '') {
      toast({
        title: t("enterCode"),
        description: t("enterCodeDesc"),
        variant: "destructive",
      });
      return;
    }

    // Code is validated - call assignment function
    await assignCurrentCleanerByCode(code.trim());
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p>{t("loading")}</p>
      </div>
    );
  }

  const isAssigning = status === "assigning";
  const isSavingLanguage = updateLanguage.isPending;

  // Name collection step (for users without first_name)
  if (step === "name") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4" dir={isRtl ? "rtl" : "ltr"}>
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">{t("welcomeToCleaning") || "Welcome!"}</CardTitle>
            <p className="text-muted-foreground mt-2">
              {t("enterYourName") || "Please enter your name to get started."}
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="first-name">{t("firstName") || "First Name"}</Label>
                <Input
                  id="first-name"
                  type="text"
                  placeholder="John"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isSavingName}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last-name">{t("lastName") || "Last Name"}</Label>
                <Input
                  id="last-name"
                  type="text"
                  placeholder="Doe"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isSavingName}
                />
              </div>
            </div>

            <Button 
              onClick={handleNameSubmit} 
              className="w-full" 
              size="lg"
              disabled={isSavingName || !firstName.trim() || !lastName.trim()}
            >
              {isSavingName ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("saving") || "Saving..."}
                </>
              ) : (
                t("continue") || "Continue"
              )}
            </Button>

            <div className="pt-4 border-t">
              <p className="text-xs text-center">
                <button
                  onClick={async () => {
                    await signOut();
                    navigate("/login", { replace: true });
                  }}
                  className="text-primary hover:underline"
                  disabled={isSavingName}
                >
                  {t("notYourAccount") || "Not your account? Switch account"}
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Language selection step
  if (step === "language") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4" dir={isRtl ? "rtl" : "ltr"}>
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Globe className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">{t("chooseLanguage")}</CardTitle>
            <p className="text-muted-foreground mt-2">
              {t("selectLanguage")}
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="language">{t("language")}</Label>
              <LanguageSelector
                value={selectedLanguage}
                onChange={handleLanguageChange}
              />
            </div>

            <Button 
              onClick={handleLanguageSubmit} 
              className="w-full" 
              size="lg"
              disabled={isSavingLanguage}
            >
              {isSavingLanguage ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("saving")}
                </>
              ) : (
                t("continue")
              )}
            </Button>

            <div className="pt-4 border-t">
              <p className="text-xs text-center">
                <button
                  onClick={async () => {
                    await signOut();
                    navigate("/login", { replace: true });
                  }}
                  className="text-primary hover:underline"
                  disabled={isSavingLanguage}
                >
                  {t("notYourAccount")}
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Property assignment step
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" dir={isRtl ? "rtl" : "ltr"}>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">{t("welcomeCleaner")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-center text-muted-foreground">
            {t("scanInstructions")}
          </p>

          {showScanner ? (
            <div className="space-y-4">
              <div 
                id="qr-reader" 
                className="w-full rounded-lg overflow-hidden bg-black"
                style={{ minHeight: "300px" }}
              />
              <Button variant="outline" onClick={stopScanner} className="w-full" disabled={isAssigning}>
                <X className="w-4 h-4 mr-2" />
                {t("cancelScanning")}
              </Button>
            </div>
          ) : showManualInput ? (
            <form onSubmit={handleManualSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="property-code">{t("propertyCode")}</Label>
                <Input
                  id="property-code"
                  placeholder={t("enterPropertyCode")}
                  value={propertyCode}
                  onChange={(e) => setPropertyCode(e.target.value)}
                  autoFocus
                  disabled={isAssigning}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowManualInput(false)}
                  className="flex-1"
                  disabled={isAssigning}
                >
                  {t("back")}
                </Button>
                <Button type="submit" className="flex-1" disabled={isAssigning}>
                  {isAssigning ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t("assigning")}
                    </>
                  ) : (
                    t("submit")
                  )}
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-3">
              <Button onClick={startScanner} className="w-full" size="lg" disabled={isAssigning}>
                <Camera className="w-5 h-5 mr-2" />
                {t("scanQrCode")}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowManualInput(true);
                  setShowScanner(false);
                }}
                className="w-full"
                size="lg"
                disabled={isAssigning}
              >
                <Keyboard className="w-5 h-5 mr-2" />
                {t("enterCodeManually")}
              </Button>
            </div>
          )}

          <div className="pt-4 border-t space-y-3">
            <p className="text-xs text-center text-muted-foreground">
              {t("noQrCode")}
            </p>
            <p className="text-xs text-center">
              <button
                onClick={async () => {
                  await signOut();
                  navigate("/login", { replace: true });
                }}
                className="text-primary hover:underline"
                disabled={isAssigning}
              >
                {t("notYourAccount")}
              </button>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CleanerOnboarding;